import {
  type LeaveAbsenceType,
  type LeaveCalendarDay,
  type LeaveCalendarEntry,
} from '@gb-ticket/shared-types';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

export { WEEKDAYS };

export function paddedMonthSpan(year: number, month: number): { from: string; to: string } {
  const bounds = monthBounds(year, month);
  const start = new Date(`${bounds.from}T00:00:00.000Z`);
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const end = new Date(`${bounds.to}T00:00:00.000Z`);
  end.setUTCDate(end.getUTCDate() + ((7 - end.getUTCDay()) % 7));
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

/** ISO week of a YYYY-MM-DD date. */
export function isoWeekNumber(iso: string): number {
  const date = new Date(`${iso}T00:00:00.000Z`);
  const thursday = new Date(date);
  thursday.setUTCDate(thursday.getUTCDate() + 3 - ((date.getUTCDay() + 6) % 7));
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 3 - ((firstThursday.getUTCDay() + 6) % 7));
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / 604800000);
}

export function monthBounds(year: number, month: number): { from: string; to: string } {
  const pad = (value: number) => String(value).padStart(2, '0');
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(last)}`,
  };
}

export function monthLabel(year: number, month: number): string {
  return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(year, month - 1, 15)),
  );
}

export function monthName(month: number): string {
  return new Intl.DateTimeFormat('de-DE', { month: 'long' }).format(
    new Date(Date.UTC(2026, month - 1, 15)),
  );
}

function weekdayMondayFirst(year: number, month: number, day: number): number {
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 ? 6 : weekday - 1;
}

export function buildCalendarCells(
  year: number,
  month: number,
): { date: string | null; day: number | null }[] {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const pad = (value: number) => String(value).padStart(2, '0');
  const prefix = weekdayMondayFirst(year, month, 1);
  const cells: { date: string | null; day: number | null }[] = [];
  for (let index = 0; index < prefix; index += 1) {
    cells.push({ date: null, day: null });
  }
  for (let day = 1; day <= last; day += 1) {
    cells.push({ date: `${year}-${pad(month)}-${pad(day)}`, day });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ date: null, day: null });
  }
  return cells;
}

export type LeaveYearDay = {
  date: string;
  day: number;
  /** UTC weekday: 0 = Sunday … 6 = Saturday. */
  weekday: number;
};

export type LeaveYearRow = {
  month: number;
  days: LeaveYearDay[];
};

/** Twelve month rows; each row starts on day 1 (columns are not weekday-aligned). */
export function buildYearRows(year: number): LeaveYearRow[] {
  const pad = (value: number) => String(value).padStart(2, '0');
  const rows: LeaveYearRow[] = [];
  for (let month = 1; month <= 12; month += 1) {
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const days: LeaveYearDay[] = [];
    for (let day = 1; day <= last; day += 1) {
      const date = `${year}-${pad(month)}-${pad(day)}`;
      days.push({
        date,
        day,
        weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
      });
    }
    rows.push({ month, days });
  }
  return rows;
}

export type SchoolHolidaySpan = {
  nameDe: string;
  startsOn: string;
  endsOn: string;
};

/** Inclusive span lookup. Empty when the area is off or the year has no table. */
export function schoolHolidayNameOn(
  date: string,
  spans: readonly SchoolHolidaySpan[],
): string | null {
  return spans.find((span) => span.startsOn <= date && date <= span.endsOn)?.nameDe ?? null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value);
}

/** Inclusive calendar range; swapping when the second day is earlier. */
export function orderedIsoRange(a: string, b: string): { from: string; to: string } {
  return a <= b ? { from: a, to: b } : { from: b, to: a };
}

export function dateInIsoRange(date: string, from: string | null, to: string | null): boolean {
  if (!from) {
    return false;
  }
  const end = to ?? from;
  const range = orderedIsoRange(from, end);
  return date >= range.from && date <= range.to;
}

export function filterYearAbsences<T extends { absenceTypeId: string | null; status: string }>(
  absences: readonly T[],
  absenceTypeId: string,
  status: '' | 'approved' | 'submitted',
): T[] {
  return absences.filter((absence) => {
    if (absenceTypeId && absence.absenceTypeId !== absenceTypeId) {
      return false;
    }
    if (status && absence.status !== status) {
      return false;
    }
    return true;
  });
}

export function hasAnyAbsence(days: readonly LeaveCalendarDay[]): boolean {
  return days.some((day) => day.entries.length > 0);
}

export function berlinTodayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
}

export function matchesCalendarEntry(
  entry: LeaveCalendarEntry,
  type: LeaveAbsenceType | null,
  nameQuery: string,
): boolean {
  const query = nameQuery.trim().toLowerCase();
  if (query.length > 0 && !entry.displayName.toLowerCase().includes(query)) {
    return false;
  }
  if (!type) {
    return true;
  }
  return entry.kind === type.kind;
}

export function filterCalendarDays(
  days: readonly LeaveCalendarDay[],
  type: LeaveAbsenceType | null,
  nameQuery: string,
): LeaveCalendarDay[] {
  if (!type && nameQuery.trim().length === 0) {
    return [...days];
  }
  return days.map((day) => ({
    ...day,
    entries: day.entries.filter((entry) => matchesCalendarEntry(entry, type, nameQuery)),
  }));
}

export function formatGermanDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${iso}T12:00:00`));
}

export function formatDayLabel(iso: string, holiday?: string): string {
  const base = formatGermanDate(iso);
  return holiday ? `${base}, ${holiday}` : base;
}
